import type { CompanyFilters } from "../../types";
import {
  normalizeMinMax,
  normalizeNumberArray,
  normalizeStringArray,
} from "../../utils/normalize";
import type { FilterMode } from "@/components/filters/Condition/types";
import { FILTER_MODE } from "@/components/filters/Condition/constants";
import type { VendorsSlice } from "./slice";

export function toCompanyFilters(slice: VendorsSlice): Partial<CompanyFilters> {
  type VendorFilterInput = NonNullable<
    NonNullable<CompanyFilters["vendors"]>[number]
  >;
  // Extract the GraphQL InclusionMethod type from VendorFilterInput to stay decoupled
  type GraphQLInclusionMethod = VendorFilterInput["inclusionMethod"];

  const out: VendorFilterInput[] = [];

  if (slice.inclusionVendorIds.length > 0) {
    const filter: VendorFilterInput = {
      ids: slice.inclusionVendorIds,
      // FilterMode values match GraphQL InclusionMethod enum values
      inclusionMethod: slice.inclusionMethod as GraphQLInclusionMethod,
    };
    if (slice.inclusionDepartmentIds.length > 0) {
      filter.departmentIds = slice.inclusionDepartmentIds;
    }
    if (slice.inclusionRoleIds.length > 0) {
      filter.roleIds = slice.inclusionRoleIds;
    }
    if (slice.inclusionInstallAgeMonths) {
      filter.installAgeMonths = slice.inclusionInstallAgeMonths;
    }
    if (slice.inclusionProductCount) {
      filter.productCount = slice.inclusionProductCount;
    }
    out.push(filter);
  }

  if (slice.excludedVendorIds.length > 0) {
    out.push({
      ids: slice.excludedVendorIds,
      inclusionMethod: FILTER_MODE.NONE as GraphQLInclusionMethod,
    });
  }

  return out.length > 0 ? { vendors: out } : {};
}

export function fromCompanyFilters(filters: CompanyFilters): VendorsSlice {
  type VendorFilterInput = NonNullable<
    NonNullable<CompanyFilters["vendors"]>[number]
  >;

  const vendors = filters.vendors ?? [];

  // Type guard for non-null VendorFilter
  const isVendorFilter = (v: unknown): v is VendorFilterInput =>
    v != null && typeof v === "object";

  const validVendors = vendors.filter(isVendorFilter);

  const inclusion = validVendors.find(
    (v) => v.inclusionMethod !== FILTER_MODE.NONE
  );
  const exclusion = validVendors.find(
    (v) => v.inclusionMethod === FILTER_MODE.NONE
  );

  return {
    inclusionMethod:
      (inclusion?.inclusionMethod as FilterMode) || FILTER_MODE.ANY,
    inclusionVendorIds: normalizeNumberArray(inclusion?.ids),
    inclusionDepartmentIds: normalizeStringArray(inclusion?.departmentIds).map(
      String
    ),
    inclusionRoleIds: normalizeStringArray(inclusion?.roleIds).map(String),
    inclusionInstallAgeMonths: normalizeMinMax(inclusion?.installAgeMonths),
    inclusionProductCount: normalizeMinMax(inclusion?.productCount),
    excludedVendorIds: normalizeNumberArray(exclusion?.ids),
  };
}
